import React, { useCallback, useEffect, useRef, useState } from 'react'
import styles from './Blog.module.scss'
import Header from '../../Components/Header/Header.tsx'
import Footer from '../../Components/Footer/Footer.tsx'
import { IBlogPost } from '../../Types/Models/types.tsx'
import { collection, getDocs } from 'firebase/firestore'
import { db } from '../../firebase.js'

const POSTS_PER_PAGE = 5

const Blog = () => {
  const [posts, setPosts] = useState<IBlogPost[]>([])
  const [loading, setLoading] = useState(true)
  const [visibleCount, setVisibleCount] = useState(POSTS_PER_PAGE)
  const [expandedPosts, setExpandedPosts] = useState<Set<string>>(new Set())
  const [shownPosts, setShownPosts] = useState<Set<number>>(new Set())
  const postsRef = useRef<(HTMLElement | null)[]>([])
  const sentinelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    window.scrollTo(0, 0)

    const fetchPosts = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "blog_posts"))
        const data: IBlogPost[] = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        })) as IBlogPost[]
        setPosts(data)
      } catch (error) {
        console.error("Ошибка при получении постов:", error)
      } finally {
        setLoading(false)
      }
    }
    fetchPosts()
  }, [])

  // Animate cards on scroll via React state (not classList)
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      const newlyVisible: number[] = []
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const idx = Number(entry.target.getAttribute('data-index'))
          if (!isNaN(idx)) newlyVisible.push(idx)
          observer.unobserve(entry.target)
        }
      })
      if (newlyVisible.length > 0) {
        setShownPosts(prev => {
          const next = new Set(prev)
          newlyVisible.forEach(i => next.add(i))
          return next
        })
      }
    }, { threshold: 0.1 })

    postsRef.current.forEach((el) => {
      if (el && !el.classList.contains(styles.animation_show)) {
        observer.observe(el)
      }
    })

    return () => observer.disconnect()
  }, [visibleCount, posts])

  // Infinite scroll
  const loadMore = useCallback(() => {
    setVisibleCount(prev => {
      if (prev >= posts.length) return prev
      return prev + POSTS_PER_PAGE
    })
  }, [posts.length])

  useEffect(() => {
    if (!sentinelRef.current || loading) return
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        loadMore()
      }
    }, { rootMargin: '200px' })

    observer.observe(sentinelRef.current)
    return () => observer.disconnect()
  }, [loadMore, loading])

  const toggleExpand = (postId: string) => {
    setExpandedPosts(prev => {
      const next = new Set(prev)
      if (next.has(postId)) {
        next.delete(postId)
      } else {
        next.add(postId)
      }
      return next
    })
  }

  const scrollToPost = (index: number) => {
    postsRef.current[index]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  const visiblePosts = posts.slice(0, visibleCount)
  const hasMore = visibleCount < posts.length

  return (
    <div className={styles.Blog}>
      <Header />

      <div className={styles.Hero}>
        <div className={styles.HeroBg} />
        <div className={styles.HeroContent}>
          <div className={styles.HeroLabel}>
            <span className={styles.HeroLabelLine} />
            <span className={styles.HeroLabelText}>Journal</span>
            <span className={styles.HeroLabelLine} />
          </div>
          <h1 className={styles.HeroTitle}>
            <span className={styles.HeroTitleLight}>Москва</span>-Сити
          </h1>
          <p className={styles.HeroSubtitle}>Всё о жизни, развлечениях и отдыхе в деловом центре столицы</p>
        </div>
      </div>

      <div className={styles.FeedSection}>
        {posts.length > 0 &&
          <nav className={styles.SideNav}>
            <span className={styles.SideNavTitle}>Содержание</span>
            <div className={styles.SideNavLine} />
            {posts.map((post, i) => (
              <button
                key={post.id}
                className={`${styles.SideNavItem} ${i < visibleCount ? '' : styles.SideNavItemHidden}`}
                onClick={() => {
                  if (i >= visibleCount) setVisibleCount(i + POSTS_PER_PAGE)
                  setTimeout(() => scrollToPost(i), 100)
                }}
              >
                <span className={styles.SideNavIndex}>{String(i + 1).padStart(2, '0')}</span>
                <span className={styles.SideNavText}>{post.title}</span>
              </button>
            ))}
          </nav>
        }

        <div className={styles.Feed}>
          {loading ? (
            <div className={styles.Loading}>
              <div className={styles.Spinner} />
            </div>
          ) : posts.length === 0 ? (
            <p className={styles.Empty}>Посты скоро появятся</p>
          ) : (
            <>
              {visiblePosts.map((post, index) => {
                const isExpanded = expandedPosts.has(post.id)
                const isShown = shownPosts.has(index)
                return (
                  <article
                    key={post.id}
                    data-index={index}
                    ref={el => { postsRef.current[index] = el }}
                    className={`${styles.PostCard} ${isShown ? styles.PostCardVisible : styles.PostCardHidden} ${isExpanded ? styles.PostCardExpanded : ''}`}
                    style={{ transitionDelay: `${(index % POSTS_PER_PAGE) * 0.1}s` }}
                  >
                    <div className={styles.PostImageWrapper}>
                      <img
                        className={styles.PostImage}
                        src={post.image}
                        alt={post.title}
                        loading="lazy"
                      />
                      <div className={styles.PostImageOverlay} />
                      <span className={styles.PostTag}>{post.tag}</span>
                      <span className={styles.PostIndex}>{String(index + 1).padStart(2, '0')}</span>
                    </div>
                    <div className={styles.PostBody}>
                      <h3 className={styles.PostTitle}>{post.title}</h3>
                      <div className={styles.PostDivider} />
                      <div className={`${styles.PostTextWrapper} ${isExpanded ? styles.PostTextExpanded : ''}`}>
                        <div
                          className={styles.PostText}
                          dangerouslySetInnerHTML={{ __html: post.text }}
                        />
                        {!isExpanded && <div className={styles.PostTextFade} />}
                      </div>
                      <div className={styles.PostFooter}>
                        {post.locationUrl &&
                          <a
                            className={styles.PostLocation}
                            href={post.locationUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                              <circle cx="12" cy="9" r="2.5"/>
                            </svg>
                            Локация
                          </a>
                        }
                        <button
                          className={styles.ReadMore}
                          onClick={() => toggleExpand(post.id)}
                        >
                          <span>{isExpanded ? 'Свернуть' : 'Читать далее'}</span>
                          <svg
                            className={`${styles.ReadMoreArrow} ${isExpanded ? styles.ReadMoreArrowUp : ''}`}
                            width="16" height="16" viewBox="0 0 24 24"
                            fill="none" stroke="currentColor" strokeWidth="1.5"
                          >
                            <path d="M6 9l6 6 6-6"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </article>
                )
              })}

              {hasMore && <div ref={sentinelRef} className={styles.Sentinel} />}
            </>
          )}
        </div>
      </div>

      <Footer />
    </div>
  )
}

export default Blog
